/**
 * Ubica, entre las unidades pendientes de reingreso, la que corresponde a una
 * lectura del lector (DataMatrix GS1) o a un número de serie tipeado. Lógica
 * pura para poder testearla.
 *
 * Se prueban todas las interpretaciones del texto que arma el campo de
 * lectura (distribución de teclado de la PC y del lector). Para no depender
 * del separador GS, además del parseo se acepta que el texto contenga
 * `01<GTIN>` y `21<serie>` de la unidad.
 */

import { gtinNormalizado, parsearStringGs1 } from '@/domain/gs1';
import type { ReingresoPendiente } from '@/api/contracts/reingresos';

export type ResultadoLecturaReingreso =
  | { tipo: 'encontrada'; stockId: number }
  | { tipo: 'no_encontrada' }
  | { tipo: 'vacia' };

function coincide(texto: string, u: ReingresoPendiente): boolean {
  const serie = (u.serie ?? '').trim();
  if (!serie) return false;
  if (texto === serie) return true; // serie tipeada sola
  const p = parsearStringGs1(texto);
  const gtinUnidad = gtinNormalizado(u.gtin);
  const gtinLeido = gtinNormalizado(p.gtin);
  const gtinOk =
    !gtinUnidad || gtinLeido === gtinUnidad || texto.includes(`01${gtinUnidad}`);
  const serieOk = p.serie === serie || texto.includes(`21${serie}`);
  return gtinOk && serieOk;
}

export function ubicarUnidadReingreso(
  textos: readonly string[],
  unidades: readonly ReingresoPendiente[],
): ResultadoLecturaReingreso {
  const limpios = textos.map((t) => t.trim()).filter(Boolean);
  if (limpios.length === 0) return { tipo: 'vacia' };
  for (const texto of limpios) {
    const u = unidades.find((x) => coincide(texto, x));
    if (u) return { tipo: 'encontrada', stockId: u.stockId };
  }
  return { tipo: 'no_encontrada' };
}
